/**
 * The claim-level AML outcome (`craAMLCheck` + `craAMLDecisionReason`).
 *
 * These two fields are populated on 499,100 live claims and, until now, were
 * rendered nowhere. The claim drawer gated the whole AML section on
 * `amlCheckUrl`, so 334,563 live claims that hold a Pass/Fail needing no fetch
 * at all displayed "No AML check data available".
 *
 * The stored verdict is NOT a reliable read on its own, which is why this
 * returns a tone as well as the raw value:
 *   - 108 of the 112 live `Fail`s are `Framework Mapper Error : Mapper
 *     exception.` -- an upstream pipeline failure, not an AML hit. Badging those
 *     red would invent 108 AML failures.
 *   - 76 live claims say "PEPs, Sanctions, or Enforcements requiring referral"
 *     but only 4 of them say `Fail` -- the other 72 say `Pass`. A referral must
 *     surface regardless of what the verdict field says.
 *
 * The verdict is always displayed verbatim; only the colour and the note are
 * derived.
 */

export type AmlTone = 'pass' | 'fail' | 'error' | 'referral' | 'unknown';

export interface AmlOutcomeDisplay {
  /** `craAMLCheck` exactly as stored, or 'Not recorded' when only a reason exists. */
  verdict: string;
  /** `craAMLDecisionReason`, or '' when absent/placeholder. */
  reason: string;
  tone: AmlTone;
  /** Plain-English clarifier where the raw verdict would mislead. '' if none. */
  note: string;
}

/** Shape this module needs. Kept local so it accepts a Claim or a bare object. */
interface AmlFields {
  craAMLCheck?: string | null;
  craAMLDecisionReason?: string | null;
  amlCheckUrl?: string | null;
  amlcheckUrl?: string | null;
}

/**
 * Legacy rows store placeholders as literal text rather than null, so a bare
 * truthiness test is not enough. 'N/A' shows up on 145 claims in
 * craAMLDecisionReason and 'None' on 43 in craAMLCheck.
 */
const PLACEHOLDERS = new Set(['', 'null', 'none', 'nan', 'undefined', 'n/a']);

const clean = (v: string | null | undefined): string => {
  const s = String(v ?? '').trim();
  return PLACEHOLDERS.has(s.toLowerCase()) ? '' : s;
};

/**
 * True when the claim carries an AML report the summary endpoint can actually
 * resolve. `amlCheckUrl` holds the literal string 'pending' on 92 live claims
 * and 'None' on 100 more; both are truthy, so the old gate opened the modal and
 * the request then 502'd on a decode error. Requiring an http(s) URL keeps the
 * button honest.
 */
export function hasFetchableAmlReport(claim: AmlFields | null | undefined): boolean {
  if (!claim) return false;
  const url = clean(claim.amlCheckUrl) || clean(claim.amlcheckUrl);
  return /^https?:\/\//i.test(url);
}

/**
 * The claim-level outcome to display, or null when the claim genuinely holds no
 * AML decision (153,241 live claims) -- in which case the caller should keep
 * saying so.
 */
export function amlOutcome(claim: AmlFields | null | undefined): AmlOutcomeDisplay | null {
  if (!claim) return null;

  const verdict = clean(claim.craAMLCheck);
  const reason = clean(claim.craAMLDecisionReason);
  if (!verdict && !reason) return null;

  // A referral outranks the verdict field: 72 of the 76 referral claims say
  // 'Pass', so trusting the verdict would hide every one of them.
  if (/requiring referral/i.test(reason)) {
    return {
      verdict: verdict || 'Not recorded',
      reason,
      tone: 'referral',
      note: 'Flagged for referral — needs review regardless of the verdict above.',
    };
  }

  // A mapper exception is the upstream pipeline falling over, not an AML result.
  if (/mapper/i.test(reason)) {
    return {
      verdict: verdict || 'Not recorded',
      reason,
      tone: 'error',
      note: 'Upstream check error, not an AML failure — no screening completed.',
    };
  }

  const lowered = verdict.toLowerCase();
  const tone: AmlTone = lowered === 'pass' ? 'pass' : lowered === 'fail' ? 'fail' : 'unknown';

  return {
    verdict: verdict || 'Not recorded',
    reason,
    tone,
    note: '',
  };
}

/** Badge colours per tone. Exported so the test pins them to the tone, not the verdict. */
export const AML_TONE_COLOURS: Record<AmlTone, { bg: string; fg: string }> = {
  pass: { bg: '#dcfce7', fg: '#166534' },
  fail: { bg: '#fee2e2', fg: '#991b1b' },
  referral: { bg: '#fef3c7', fg: '#92400e' },
  error: { bg: '#e5e7eb', fg: '#4b5563' },
  unknown: { bg: '#e5e7eb', fg: '#4b5563' },
};
